import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { OfficerRoleEnum } from '@shared/types/officerEnum';
import { LookupOfficerDialogComponent } from '@shared/component/job/component/company-incorporation/dialog/lookup-officer-dialog/lookup-officer-dialog.component';
import { ACTIVE_USER } from '@modules/auth/providers/auth.provider';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { User } from '@modules/user/models/user.model';
import {
  CompanyDirectorInput,
  CompanyShareholderInput,
  CreateCompanyInput,
  IncorporateTypeEnum,
  OfficerCompanyType,
  OfficerType,
  OfficerTypeEnum,
  SupportedCountryType,
} from '@generated/graphql';
import { SelectOption } from '@shared/helpers/convention.helpers';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { catchError, finalize, first } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectOperationCountry } from '@modules/job/store/create-job.selectors';
import { ShareholderFormType } from '@modules/job/models/form/shareholder-form-type';
import { DirectorFormType } from '@app/modules/job/models/form/director-form-type';
import { OfficerDialogState } from './classes/officer-dialog-state.class';
import { createOfficerDialogState } from './classes/officer-dialog-state.factory';
import { ShareholderOfficerDialogState } from './classes/shareholder-officer-dialog-state.class';
import { ExistsOfficerType } from './classes/exists-officer.type';
import { OfficerService } from '@app/modules/officer/services/officer.service';
import { DateTime } from 'luxon';
import { AbstractOfficerForm } from '@shared/classes/officer/abstract-officer-form.class';
import { LinkedCurrentJobOfficerSelection } from './linked-current-job-officer.type';

export type OfficerDialogExtraData = {
  dateOfAppointment?: Date | null;
};

type OfficerDialogExtraForm = FormGroup<{
  dateOfAppointment: FormControl<Date | null>;
}>;

type DetailMode = 'NEW' | 'USE_EXISTING';
type CompanyOfficerInput = CompanyShareholderInput | CompanyDirectorInput;
type LookupOfficerSelection = {
  officer: OfficerType;
  type: OfficerTypeEnum;
};

export type AddOfficerDialogData = {
  incorporateType?: IncorporateTypeEnum;
  officerRole: OfficerRoleEnum;
  companyCapitals?: FormArray;
  editData?:
  | CreateCompanyInput['shareholders'][0]
  | CreateCompanyInput['directors'][0];
  shareholders?: FormArray<FormGroup<ShareholderFormType>>;
  directors?: FormArray<FormGroup<DirectorFormType>>;
  shareholderEditIndex?: number;
  linkedCurrentJobOfficer?: LinkedCurrentJobOfficerSelection;
  exceptIds: string[];
};

export type AddOfficerDialogResult = {
  officerForm: AbstractOfficerForm;
  refId: string;
  extraData?: OfficerDialogExtraData;
  linkedCurrentJobOfficer?: LinkedCurrentJobOfficerSelection;
};

@Component({
  selector: 'app-add-officer-dialog',
  templateUrl: './add-officer-dialog.component.html',
  styleUrls: ['./add-officer-dialog.component.scss'],
})
export class AddOfficerDialogComponent implements OnInit, OnDestroy {
  readonly officerType = OfficerTypeEnum;
  readonly companyCapitals: FormArray;
  readonly officerRole: OfficerRoleEnum;

  selectedOfficerType = OfficerTypeEnum.Individual;
  readonly officerTypeOptions: SelectOption[] = [
    {
      label: OfficerTypeEnum.Individual.toString().toLowerCase(),
      value: OfficerTypeEnum.Individual,
    },
    {
      label: OfficerTypeEnum.Corporate.toString().toLowerCase(),
      value: OfficerTypeEnum.Corporate,
    },
  ];
  detailMode: DetailMode = 'NEW';
  detailModeOptions: SelectOption[] = [];
  countryOfIncorporation: SupportedCountryType;

  dialogState!: OfficerDialogState;

  selectedRefId?: string;
  selectedLinkedCurrentJobOfficer?: LinkedCurrentJobOfficerSelection;

  customerExistingOfficers: OfficerType[] = [];

  loadingCustomerOfficers$ = new BehaviorSubject(true);

  get hasDateOfAppointment(): boolean {
    return (
      this.data.incorporateType === IncorporateTypeEnum.Transfer &&
      this.officerRole === OfficerRoleEnum.DIRECTOR
    );
  }

  get isUsingExistingDetails(): boolean {
    return this.detailMode === 'USE_EXISTING';
  }

  get isSaveDisabled(): boolean {
    return !!(
      (this.extraForm && !this.extraForm.valid) ||
      !this.dialogState.isOfficerFormValid()
    );
  }

  extraForm: OfficerDialogExtraForm | null = null;

  constructor(
    private _fb: FormBuilder,
    @Inject(ACTIVE_USER) public readonly activeUser$: Observable<User>,
    @Inject(MAT_DIALOG_DATA) public data: AddOfficerDialogData,
    public dialogRef: DialogRef<AddOfficerDialogComponent>,
    private dialog: MatDialog,
    private store: Store,
    private officerService: OfficerService,
  ) {
    this.companyCapitals = data.companyCapitals ?? this._fb.array([]);
    this.officerRole = this.data.officerRole;
  }

  ngOnInit(): void {
    this.detailModeOptions = this.buildDetailModeOptions();
    this.loadOperationCountry();
  }

  private loadOperationCountry(): void {
    this.store
      .select(selectOperationCountry)
      .pipe(first())
      .subscribe((countryOfIncorporation) => {
        this.countryOfIncorporation = countryOfIncorporation;
        this.initializeDialogStateFactory();
        this.initializeDialogState();
      });
  }

  private initializeDialogStateFactory(): void {
    this.dialogState = createOfficerDialogState(
      this.officerRole,
      this.data.editData ? 'edit' : 'new',
    );
    this.dialogState.countryOfIncorporation = this.countryOfIncorporation;
  }

  private initializeExtraForm(): void {
    if (this.hasDateOfAppointment) {
      this.extraForm = this._fb.group({
        dateOfAppointment: this._fb.control<Date>(null, Validators.required),
      });

      const { editData } = this.data;

      if (editData && (editData as CompanyDirectorInput).dateOfAppointment) {
        this.extraForm.controls.dateOfAppointment.setValue(
          (editData as CompanyDirectorInput).dateOfAppointment,
        );
      }
    }
  }

  private initializeDialogState(): void {
    this.dialogState.setCurrentJobOfficers(
      this.data.directors || this.data.shareholders || undefined,
    );

    this.loadCustomerExistingOfficers();

    if (this.data.editData) {
      this.initializeFromEditData(this.data.editData);
    } else {
      this.dialogState.createOfficerForm();
    }

    this.bindShareholderControls();

    this.initializeExtraForm();
  }

  private loadCustomerExistingOfficers(): void {
    this.activeUser$.pipe(first()).subscribe((user) => {
      if (!user.isCustomer()) {
        this.loadingCustomerOfficers$.next(false);
        return;
      }

      this.loadingCustomerOfficers$.next(true);

      this.officerService
        .allOfficersByCustomer(this.countryOfIncorporation.country.code)
        .pipe(
          first(),
          catchError(() => of([])),
          finalize(() => this.loadingCustomerOfficers$.next(false)),
        )
        .subscribe((officers) => {
          this.customerExistingOfficers = officers;
          this.dialogState.setExistingOfficers(officers);
        });
    });
  }

  private initializeFromEditData(officer: CompanyOfficerInput): void {
    this.selectedOfficerType = officer.officerType as OfficerTypeEnum;
    this.dialogState
      .setSelectedOfficerType(this.selectedOfficerType)
      .createOfficerForm()
      .prepareEditData(officer);

    if (!officer.refId) {
      return;
    }

    this.selectedRefId = officer.refId;
    this.selectedLinkedCurrentJobOfficer = this.data.linkedCurrentJobOfficer;
    this.detailMode = 'USE_EXISTING';
    this.dialogState.disableOfficerFormForExistingReference();
  }

  private bindShareholderControls(): void {
    if (
      this.data.shareholders &&
      this.dialogState instanceof ShareholderOfficerDialogState
    ) {
      this.dialogState.setShareholderControls(this.data.shareholders);
    }
  }

  getShareDistributionsForm(): FormGroup {
    return (
      this.dialogState as ShareholderOfficerDialogState
    ).getShareDistributionsForm();
  }

  getShareholderControls(): FormArray<FormGroup<ShareholderFormType>> {
    return (this.dialogState as ShareholderOfficerDialogState)
      .shareholderControls;
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  onOfficerTypeChange(event: MatRadioChange): void {
    this.selectedOfficerType = event.value;
    this.dialogState
      .setSelectedOfficerType(this.selectedOfficerType)
      .createOfficerForm();
  }

  onDetailModeChange(event: MatRadioChange): void {
    this.detailMode = event.value;

    if (this.detailMode !== 'NEW') {
      return;
    }

    Promise.resolve().then(() => {
      this.dialogState.getOfficerForm().enable();
      this.dialogState.getOfficerForm().reset();
    });

    this.selectedRefId = undefined;
    this.selectedLinkedCurrentJobOfficer = undefined;
  }

  private buildDetailModeOptions(): SelectOption[] {
    return [
      {
        label: `New ${this.officerRole.toString().toLowerCase()}`,
        value: 'NEW',
      },
      {
        label: 'Existing Shareholders / Directors',
        value: 'USE_EXISTING',
      },
    ];
  }

  onOpenLookupOfficer(): void {
    const lookupOfficerDialog = this.dialog.open(LookupOfficerDialogComponent, {
      width: '50%',
      data: {
        countryOfIncorporation: this.countryOfIncorporation,
        hideCorporate: this.shouldHideCorporateLookupOption(),
        exceptOfficerIds: this.data.exceptIds,
      },
    });

    lookupOfficerDialog
      .afterClosed()
      .pipe(first())
      .subscribe((result: LookupOfficerSelection | undefined) => {
        if (result) {
          this.applyLookupOfficer(result.officer, result.type);
        }
      });
  }

  canSelectOfficerType(officerTypeOption: SelectOption): boolean {
    const excepts = ['SG'];

    return !(
      officerTypeOption.value === OfficerTypeEnum.Corporate &&
      this.officerRole === OfficerRoleEnum.DIRECTOR &&
      excepts.includes(this.countryOfIncorporation.country.code.toUpperCase())
    );
  }

  shouldShowDetailModeSelector(activeUser: User): boolean {
    return (
      activeUser.isCustomer() ||
      (!activeUser.isCustomer() && this.dialogState.isDirector())
    );
  }

  shouldShowExistingOfficerSelector(
    activeUser: User,
    loadingCustomerOfficers: boolean,
  ): boolean {
    if (!this.isUsingExistingDetails) {
      return false;
    }

    if (activeUser.isCustomer()) {
      return this.dialogState.existingOfficers.length > 0 || !loadingCustomerOfficers;
    }

    return true;
  }

  onExistingOfficerSelected(existsOfficer: ExistsOfficerType): void {
    const lookupOfficer = this.findCustomerExistingOfficer(existsOfficer.id);

    if (lookupOfficer) {
      this.selectedLinkedCurrentJobOfficer = undefined;
      this.applyLookupOfficer(lookupOfficer, existsOfficer.type);
      return;
    }

    const currentJobOfficerSelection =
      this.resolveExistingCreatingOfficerSelection(existsOfficer);

    if (!currentJobOfficerSelection) {
      return;
    }

    const { officer, linkedCurrentJobOfficer } = currentJobOfficerSelection;

    this.selectedOfficerType = existsOfficer.type;
    this.dialogState
      .setSelectedOfficerType(this.selectedOfficerType)
      .createOfficerForm()
      .prepareEditData(officer)
      .disableOfficerFormForExistingReference();

    this.selectedRefId = existsOfficer.id;
    this.selectedLinkedCurrentJobOfficer = linkedCurrentJobOfficer;
  }

  private applyLookupOfficer(
    officer: OfficerType,
    officerType: OfficerTypeEnum,
  ): void {
    if (
      officerType === OfficerTypeEnum.Corporate &&
      this.shouldHideCorporateLookupOption()
    ) {
      return;
    }

    this.selectedOfficerType = officerType;
    this.dialogState.setLookupData(officer, officerType);
    this.selectedRefId = officer.id;
    this.selectedLinkedCurrentJobOfficer = undefined;
  }

  private shouldHideCorporateLookupOption(): boolean {
    return (
      this.officerRole === OfficerRoleEnum.DIRECTOR &&
      this.countryOfIncorporation?.country?.code?.toUpperCase() === 'SG'
    );
  }

  private findCustomerExistingOfficer(officerId: string): OfficerType | undefined {
    const representativeOwner = this.customerExistingOfficers.find(
      (officer) =>
        'representative' in officer.officerProfile &&
        officer.officerProfile.representative.id === officerId,
    );

    if (representativeOwner) {
      return (
        representativeOwner.officerProfile as OfficerCompanyType
      ).representative;
    }

    return this.customerExistingOfficers.find(
      (officer) => officer.id === officerId,
    );
  }

  private resolveExistingCreatingOfficerSelection(
    existsOfficer: ExistsOfficerType,
  ): {
    officer: CompanyOfficerInput;
    linkedCurrentJobOfficer: LinkedCurrentJobOfficerSelection;
  } | undefined {
    if (this.dialogState.isDirector()) {
      const officer = this.findLinkedCreatingOfficer(
        this.data.shareholders?.getRawValue() as CompanyOfficerInput[] | undefined,
        existsOfficer.id,
      );

      return this.buildCurrentJobOfficerSelection(
        officer,
        existsOfficer,
        OfficerRoleEnum.SHAREHOLDER,
        true,
      );
    }

    if (this.dialogState.isShareholder()) {
      const officer = this.findLinkedCreatingOfficer(
        this.data.directors?.getRawValue() as CompanyOfficerInput[] | undefined,
        existsOfficer.id,
      );

      return this.buildCurrentJobOfficerSelection(
        officer,
        existsOfficer,
        OfficerRoleEnum.DIRECTOR,
        false,
      );
    }

    return undefined;
  }

  private buildCurrentJobOfficerSelection(
    officer: CompanyOfficerInput | undefined,
    existsOfficer: ExistsOfficerType,
    sourceRole: OfficerRoleEnum.SHAREHOLDER | OfficerRoleEnum.DIRECTOR,
    includeDateFrom: boolean,
  ): {
    officer: CompanyOfficerInput;
    linkedCurrentJobOfficer: LinkedCurrentJobOfficerSelection;
  } | undefined {
    if (!officer) {
      return undefined;
    }

    return {
      officer: this.mapRepresentativeOfficerSelection(
        officer,
        existsOfficer.type,
        includeDateFrom,
      ) as CompanyOfficerInput,
      linkedCurrentJobOfficer: {
        sourceRole,
        sourceId: officer.id,
        relation: officer.id === existsOfficer.id ? 'primary' : 'representative',
      },
    };
  }

  private findLinkedCreatingOfficer(
    officers: CompanyOfficerInput[] | undefined,
    officerId: string,
  ): CompanyOfficerInput | undefined {
    return officers?.find((officer) =>
      this.matchesExistingOfficerSelection(officer, officerId),
    );
  }

  private matchesExistingOfficerSelection(
    officer: CompanyOfficerInput,
    officerId: string,
  ): boolean {
    if (officer.id === officerId) {
      return true;
    }

    return (
      officer.officerType === OfficerTypeEnum.Corporate &&
      officer.officerCompany?.representative.id === officerId
    );
  }

  private mapRepresentativeOfficerSelection(
    officer: CompanyOfficerInput | undefined,
    selectedType: OfficerTypeEnum,
    includeDateFrom: boolean,
  ): CompanyOfficerInput | undefined {
    if (
      !officer ||
      officer.officerType !== OfficerTypeEnum.Corporate ||
      selectedType !== OfficerTypeEnum.Individual
    ) {
      return officer;
    }

    const representativeSelection: CompanyOfficerInput = {
      id: officer.id,
      officerType: officer.officerType,
      isNominee: false,
      officerPerson: officer.officerCompany.representative,
      refId: officer.officerCompany.representative.id,
    };

    if (includeDateFrom) {
      return {
        ...representativeSelection,
        dateFrom: DateTime.now().toJSDate(),
      };
    }

    return representativeSelection;
  }

  buildDialogResult(): AddOfficerDialogResult {
    const result: AddOfficerDialogResult = {
      officerForm: this.dialogState.officerFormState,
      refId: this.selectedRefId,
      extraData: this.extraForm?.getRawValue() as
        | OfficerDialogExtraData
        | undefined,
    };

    if (this.selectedLinkedCurrentJobOfficer) {
      result.linkedCurrentJobOfficer = this.selectedLinkedCurrentJobOfficer;
    }

    return result;
  }

  ngOnDestroy(): void {
    this.loadingCustomerOfficers$.complete();
  }
}
